import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  Stack,
  buildPath,
  buildTemplatePath,
  pn
} from "@compass/core";
import { ListFooter } from "components";
import {
  Permission,
  TestTypeListDto,
  TestTypeListFilter,
  TestTypeListResource
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { TestTypeListPageHeader } from "./TestTypeListPageHeader"; 
import { TestTypeTable } from "./TestTypeTable";
import { TestTypeFilter } from "../TestTypeFilter";

const routeName = "testTypes";

export const testTypeListPageRoute: PageRoute = {
  handle: "Test Types",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const TestTypeListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<TestTypeListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<TestTypeListFilter>({
      sortObjects: [{ property: pn<TestTypeListDto>("testType"), isDesc: false }],
      testTypeFilter: {
          // inactive: false
      }
  });
  const [initListFilter] = useState<TestTypeListFilter>(listFilter);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<TestTypeListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.testTypes.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.testTypes.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    ApiService.testTypes.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  const listFilterNode = useListFilter<TestTypeListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <TestTypeFilter
        filter={listFilter.testTypeFilter || {}}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.testTypeFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page
        header={
          <TestTypeListPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={testTypeListPageRoute.handle}
          />
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <TestTypeTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              testType={listItems}
              setTestType={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
              listFilter={listFilter}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
